import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const milestones = [
    { year: '2018', title: '平台成立', description: '久久在线观看正式上线，以高清视频免费观看为核心定位，开启在线视频服务之旅。' },
    { year: '2019', title: '资源突破万部', description: '视频资源库突破1万部，覆盖电影、电视剧、综艺三大品类，注册用户突破100万。' },
    { year: '2020', title: '4K超清上线', description: '全面升级技术支持，率先推出4K超清画质播放，成为行业内领先的高清视频平台。' },
    { year: '2021', title: '多端覆盖', description: '完成移动端适配，实现手机、平板、电脑多端无缝切换，随时随地久久在线观看。' },
    { year: '2022', title: '品类扩展', description: '新增动漫、纪录片、体育三大品类，覆盖六大内容板块，满足更多用户的观影需求。' },
    { year: '2023', title: '用户突破5000万', description: '久久在线观看活跃用户突破5000万，日均播放量超2000万次，成为行业头部平台。' },
    { year: '2024', title: 'AI智能推荐', description: '引入AI智能推荐算法，根据用户观看偏好精准推送内容，提升用户体验。' },
    { year: '2025', title: '全球化加速', description: '启动全球化战略，支持多语言界面，将久久在线观看的优质服务推广至全球用户。' },
  ];

  const testimonials = [
    {
      name: '王先生',
      role: '资深影迷',
      content: '久久在线观看是我最常用的视频平台，资源丰富更新快，画质也很清晰。不用下载任何软件就能看，真的非常方便！',
      rating: 5,
    },
    {
      name: '李女士',
      role: '追剧达人',
      content: '每天下班回家第一件事就是打开久久在线观看追剧，更新的速度很快，热门剧集基本当天就能看到，好评！',
      rating: 5,
    },
    {
      name: '张同学',
      role: '大学生',
      content: '作为学生党，免费高清的久久在线观看简直太友好了。宿舍里用手机看动漫，画质一点都不差，推荐给同学们了。',
      rating: 5,
    },
    {
      name: '陈先生',
      role: '体育爱好者',
      content: 'NBA比赛经常半夜直播，第二天在久久在线观看上找高清回放，画面清晰流畅，看球体验相当不错。',
      rating: 5,
    },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '久久在线观看',
    url: 'https://mobiang.cn/',
    description: '久久在线观看是一个高清视频免费畅享平台，提供电影、电视剧、综艺、动漫等海量视频资源。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9999',
      contactType: 'customer service',
      email: 'contact@jiujiuzaixian.com',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '望京SOHO T1',
      addressLocality: '北京',
      addressRegion: '北京',
      addressCountry: 'CN',
    },
    sameAs: [
      'https://weibo.com/jiujiuzaixian',
      'https://www.bilibili.com',
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 久久在线观看',
    description: '久久在线观看品牌介绍、发展历程、用户口碑与联系方式。高清视频免费畅享平台。',
    url: 'https://mobiang.cn/about',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />

            <div className="mt-6 mb-10">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">关于我们</h1>
              <p className="text-sm text-foreground-500 mt-2">久久在线观看 — 高清视频免费畅享平台</p>
            </div>

            {/* Brand Introduction */}
            <section className="mb-14">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">品牌介绍</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    久久在线观看成立于2018年，是一家专注于高清视频在线播放的互联网平台。我们致力于为广大用户提供海量、高清、免费的在线视频资源，涵盖<strong>电影、电视剧、综艺、动漫、纪录片、体育赛事</strong>等六大核心品类。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    久久在线观看始终坚持<strong>"免费、高清、流畅"</strong>的服务理念。用户无需注册、无需下载任何软件或插件，打开浏览器即可立即享受高品质的观影体验。我们采用全球CDN加速技术，确保视频加载迅速、播放流畅，支持从标清到4K超清多种画质选择。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    截至目前，久久在线观看已积累<strong>超过10万部高清视频资源</strong>，活跃用户突破<strong>5000万</strong>，日均播放量超过<strong>2000万次</strong>。久久在线观看已成为国内领先的在线视频平台之一，深受广大用户的喜爱与信赖。
                  </p>
                </div>
                <div className="rounded-xl overflow-hidden aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20space%20with%20large%20screens%20displaying%20video%20content%2C%20warm%20cozy%20lighting%2C%20creative%20tech%20company%20atmosphere%2C%20professional%20yet%20relaxed%20vibe%2C%20clean%20lines%2C%20natural%20wood%20and%20soft%20textiles&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                    alt="久久在线观看品牌办公环境"
                    title="久久在线观看 – 品牌办公环境"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </section>

            {/* Milestones */}
            <section className="mb-14">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-8 text-center">发展历程</h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2 hidden md:block"></div>
                <div className="space-y-8">
                  {milestones.map((item, index) => (
                    <div key={index} className={`flex flex-col md:flex-row gap-4 md:gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                        <div className={`hidden md:block ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">{item.year}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 md:justify-center">
                        <span className="md:hidden px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">{item.year}</span>
                        <div className="w-3 h-3 rounded-full bg-primary-500 shrink-0 hidden md:block"></div>
                      </div>
                      <div className="flex-1">
                        <div className="bg-background-100 rounded-xl p-5">
                          <h3 className="text-sm font-semibold text-foreground-800 mb-1.5">{item.title}</h3>
                          <p className="text-xs text-foreground-500 leading-relaxed">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Testimonials */}
            <section className="mb-14">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-8 text-center">用户口碑</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {testimonials.map((item, index) => (
                  <div key={index} className="bg-background-100 rounded-xl p-5">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: item.rating }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{item.content}&rdquo;</p>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mb-14">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-8 text-center">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-500">contact@jiujiuzaixian.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                      <i className="ri-phone-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1">客服电话</h3>
                      <p className="text-sm text-foreground-500">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                      <i className="ri-map-pin-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1">公司地址</h3>
                      <p className="text-sm text-foreground-500">北京市朝阳区望京SOHO T1</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 shrink-0">
                      <i className="ri-time-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1">工作时间</h3>
                      <p className="text-sm text-foreground-500">周一至周日 9:00 - 21:00</p>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden h-[300px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3059.7335725720817!2d116.48148931545091!3d39.99696417420975!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f0541e3de6efab%3A0xbcfbea99eec1a214!2sWangjing%20SOHO!5e0!3m2!1szh-CN!2sus!4v1691482800000!5m2!1szh-CN!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="久久在线观看公司位置"
                    aria-label="久久在线观看北京办公室地图"
                  ></iframe>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}